import axios from "axios";
import { env, Native } from "@hb/mbridge";
import { getTokenAndKey } from "@/utils/index";
import { handleInvalidToken } from "@/api/axios";
import {
  interfaceConfig,
  errorMessage,
  // apiEnums,
} from "../constants/apiConstants";
import { UiInterface } from "../uiMgr";
import {
  getAppEnv,
  getCurEnvData,
  getSystemCode,
} from "../env/env";
import { appQueryConfig } from "../utils/url";
import { NetErrorType, NetError } from "./err";
import logger from "../utils/logger";

/**
 * 网络请求自定义错误类型
 */
export type CusNetError = {
  netErrType: NetErrorType;
  netErrMsg: string;
};

export interface ServerConfig {
  serverDomain: string;
}

export interface ApiConfig {
  timeout: number;
  /** 是否需要显示loading遮罩 */
  isShowLoading: boolean;
  /** 显示loading遮罩的显示内容 */
  loadingShow?: string;
  requestMethod: string;
  header?: any;
  isResolveError: boolean;
  isShowNetErr: boolean;
  /** 是否展示服务端业务错误提示 */
  showServerErr?: boolean;
  baseUrlFn?: () => string;
  /** 是否无需登录 */
  noLogin?: boolean;
}

export interface InterfaceConfig {
  default: ApiConfig;
  [propName: string]: Partial<ApiConfig> | ApiConfig;
}

const ConfigPropMap: Record<string, string> = {
  isShowLoading: "showLoading",
};

export interface ResErrorData {
  code: number;
  msg: string;
}

export interface ResData<T> extends ResErrorData {
  data: T;
}

export class HttpMgr {
  private _serverConfig: ServerConfig = { serverDomain: "" };

  private _interfaceConfig: InterfaceConfig = interfaceConfig;

  private _uiMgr!: UiInterface | undefined;

  private _requestCount = 0;

  // 重新登录状态管理
  private _reloginPromise: Promise<void> | null = null;

  private _curData: any = {
    action: "",
    requestParams: {
      userToken: "",
    },
    requestMethod: "",
    header: {},
    showLoading: true,
    timeout: 0,
    loadingShow: "",
    isResolveError: false,
    isShowNetErr: true,
    showServerErr: true, // 默认展示服务端错误
    noLogin: false,
    _retryCount: 0, // 添加重试计数器，防止无限递归
  };

  // private _tempErr: Error | CusNetError | any = null; // ❌ 注释掉：会导致并发请求状态污染, 比如A接口错误状态展示给B接口使用

  public init(options: {
    interfaceConfig: InterfaceConfig;
    uiMgr?: UiInterface;
  }) {
    // 初始化处理
    this._interfaceConfig = options.interfaceConfig;
    this._uiMgr = options.uiMgr;
  }

  // httpUtil 暴露给外面的接口
  async request(
    action: string,
    requestParams = {},
    {
      requestMethod = this._interfaceConfig.default.requestMethod,
      showLoading = this._interfaceConfig.default.isShowLoading, // 是否需要遮罩
      header = this._interfaceConfig.default.header, // 默认header
    } = {},
  ) {
    // ✅ 使用局部变量，每个请求独立，避免并发请求状态污染
    let requestError: Error | CusNetError | any = null;

    this._preHandle({
      action,
      requestParams,
      requestMethod,
      showLoading,
      header,
    });

    // ✅ 保存当前请求的 _curData 快照，避免被其他并发请求覆盖
    const currentRequestData = {
      action: this._curData.action,
      showServerErr: this._curData.showServerErr,
      isShowNetErr: this._curData.isShowNetErr,
    };

    try {
      // 请求
      this._callHook("before");
      const responseData = await this._handleRequest();
      const result: any = await this._handleResponse(responseData);
      this._callHook("after", { url: action, data: result });
      return result;
    } catch (err) {
      // 当是网络超时错误时，且接口配置为不展示接口错误提示，则将错误置为null
      if (
        err
        && err instanceof Error
        && err.message === errorMessage.timeout
        && !currentRequestData.isShowNetErr
      ) {
        requestError = null;
      } else {
        requestError = err; // 记录错误
      }

      throw err; // 然后将错误继续抛出去
    } finally {
      this._finalHandle();
      if (requestError) {
        // 弱提示错误
        if (
          requestError
          && requestError.netErrType === NetErrorType.serverErr
        ) {
          // 使用 showServerErr 控制服务端业务错误提示
          if (this._uiMgr && currentRequestData.showServerErr) {
            this._uiMgr.showToast(requestError.netErrMsg);
          }
        }
      }
    }
  }

  private _showMask() {
    if (this._curData.showLoading) {
      this._requestCount += 1;
    }
    if (this._requestCount > 0) {
      if (this._uiMgr) {
        this._uiMgr.showLoading();
      }
    }
  }

  private _hideMask() {
    if (!this._curData.showLoading) {
      return;
    }
    if (this._curData.showLoading) {
      this._requestCount -= 1;
    }
    if (this._requestCount <= 0) {
      this._requestCount = 0;
      if (this._uiMgr) {
        this._uiMgr.hideLoading();
      }
    }
  }

  // 预处理
  private _preHandle({
    action,
    requestParams,
    requestMethod,
    showLoading,
    header,
  }: {
    [propName: string]: any;
  }) {
    this._curData.action = action;
    this._curData.requestParams = requestParams;
    this._curData.requestMethod = requestMethod;
    this._curData.showLoading = showLoading;
    this._curData.header = header;
    this._curData.timeout = this._interfaceConfig.default.timeout;
    this._curData.loadingShow = "正在加载中...";
    this._curData.isResolveError = this._interfaceConfig.default.isResolveError;
    this._curData.isShowNetErr = this._interfaceConfig.default.isShowNetErr;
    // 默认为 true，兼容未配置的情况
    this._curData.showServerErr = this._interfaceConfig.default.showServerErr !== false;
    this._curData.noLogin = this._interfaceConfig.default.noLogin;
    this._curData._retryCount = 0; // 每次新请求重置重试计数器

    // 处理 showServerErr 参数
    if ("showServerErr" in requestParams) {
      this._curData.showServerErr = requestParams.showServerErr !== false;
      delete requestParams.showServerErr; // 从请求参数中删除，不发送到服务器
    }

    // 根据配置文件重写部分参数 start
    const customConfig = this._interfaceConfig[action];

    if (customConfig) {
      [
        "timeout",
        "requestMethod",
        "header",
        "isShowLoading",
        "loadingShow",
        "isResolveError",
        "isShowNetErr",
        "showServerErr",
        "noLogin",
      ].forEach((prop) => {
        if (Object.prototype.hasOwnProperty.call(customConfig, prop)) {
          this._curData[ConfigPropMap[prop] || prop] = customConfig[prop as keyof ApiConfig];
        }
      });
    }

    if (
      customConfig
      && Object.prototype.hasOwnProperty.call(customConfig, "baseUrlFn")
      && typeof customConfig.baseUrlFn === "function"
    ) {
      this._curData.baseUrl = customConfig.baseUrlFn();
    } else {
      this._curData.baseUrl = "";
    }

    // 根据配置文件重写部分参数 end
    this._showMask(); // 打开遮罩
  }

  // 请求
  private async _handleRequest() {
    const envData = getCurEnvData();

    // 并发安全：在任意 await 之前快照本次请求的 _curData，避免被后续并发请求覆盖
    const curAction = this._curData.action;
    const curBaseUrl = this._curData.baseUrl || envData.serverUrl;
    const curRequestMethod = this._curData.requestMethod;
    const curHeader = this._curData.header;
    const curNoLogin = this._curData.noLogin;
    const requestParams = { ...this._curData.requestParams };

    if (!curNoLogin) {
      const token = await this._getRequestToken();
      if (token) {
        requestParams.token = token;
      }
    }

    Object.assign(requestParams, {
      action: curAction,
      systemCode: getSystemCode(),
      adsource: appQueryConfig.adsource,
    });
    logger(
      "调试adsource",
      requestParams,
      window.location.href,
    );

    // 环境判断：只在非生产环境才拼接 action 参数，生产环境保持原样
    let url: string;
    const isProd = getAppEnv() === "pro";

    if (isProd) {
      // 生产环境：保持原有逻辑，不拼接 action
      url = curBaseUrl;
    } else {
      // 测试环境：拼接 action 参数到 URL（方便调试）
      const separator = curBaseUrl.includes("?") ? "&" : "?";
      url = `${curBaseUrl}${separator}action=${curAction}`;
    }

    return axios({
      url, // 历史  url: this._curData.baseUrl || envData.serverUrl,
      data: requestParams,
      method: curRequestMethod,
      headers: curHeader,
    });
  }

  private async _handleResponse(responseData: any): Promise<any> {
    if (!responseData || !responseData.data) {
      return Promise.reject(
        new NetError(errorMessage.serverError, NetErrorType.serverErr),
      );
    }

    const serverData: {
      code: number | string;
      msg: string;
      data: any;
    } = responseData.data;

    if (serverData.code === 0 || serverData.code === "0") {
      return Promise.resolve(serverData);
    }
    if (serverData.code === 103 || serverData.code === "103") {
      // ⭐ 参考 axios.js 的处理流程，完全遵循项目通用的 Token 失效处理
      logger(`[httpMgr] 接口返回103错误 - token过期，action:${this._curData.action}`);
      // 1️⃣ 支付宝浏览器特殊处理
      if (env.isAlipayBrowser) {
        Native.invalidToken(); // 清除缓存token
      }

      // 2️⃣ H5/Web 环境：直接调用 handleInvalidToken 跳转登录页（不重试）
      if (!env.isAliMP && !env.isAliMPMonitor && !env.isEasyBikeApp) {
        logger("[httpMgr] H5 environment, calling handleInvalidToken...");
        handleInvalidToken();
        // handleInvalidToken 会跳转登录页，抛出错误停止当前请求
        throw new NetError(
          "登录已失效，请重新登录",
          NetErrorType.serverErr,
        );
      }

      // 3️⃣ 客户端/小程序环境：检查是否需要重新登录
      const needLogin = !this._curData.noLogin;
      if (needLogin) {
        // 如果已经有重新登录在进行，等待它完成
        if (this._reloginPromise) {
          logger("[httpMgr] Relogin already in progress, waiting for it to complete...");
          await this._reloginPromise;
          // 重新登录完成后，重新发起当前请求
          return this._retryCurrentRequest();
        }

        // 开始新的重新登录流程
        this._reloginPromise = this._handleClientRelogin();

        try {
          await this._reloginPromise;
          // 重新登录成功后，重新发起当前请求
          return this._retryCurrentRequest();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("[httpMgr] Relogin failed:", error);
          throw new NetError(
            "登录已失效，请重新登录",
            NetErrorType.serverErr,
          );
        } finally {
          this._reloginPromise = null;
        }
      } else {
        // 不需要登录的接口，直接抛出错误
        throw new NetError(
          "登录已失效",
          NetErrorType.serverErr,
        );
      }
    } else if (this._curData.isResolveError) {
      return Promise.resolve({
        errCode: "",
        errMsg: serverData.msg,
      });
    }

    if (serverData.msg) {
      return Promise.reject(
        new NetError(serverData.msg, NetErrorType.serverErr),
      );
    }

    return Promise.reject(
      new NetError(
        serverData.msg || errorMessage.serverError,
        NetErrorType.serverErr,
      ),
    );
  }

  // 收尾处理
  private _finalHandle() {
    this._hideMask(); // 关闭遮罩
  }

  private _callHook(hookeName: "before" | "after", params?: any) {
    const handles: { (...args: any[]): void }[] = HttpMgr.hooks[hookeName];
    if (!handles) {
      return;
    }
    for (let i = 0; i < handles.length; i += 1) {
      handles[i](params);
    }
  }

  /**
   * ⭐ 获取请求所需的 token
   * 优先从 window.$$user 同步获取（性能最优），缓存未命中时调用 getTokenAndKey
   * @returns token 字符串，获取失败返回 null
   */
  private async _getRequestToken(): Promise<string | null> {
    try {
      // 1️⃣ 优先从 window.$$user 同步获取（性能最优）
      // window.$$user 在 main.js 初始化，由 getTokenAndKey 维护
      if (window.$$user && window.$$user.token) {
        logger(`[httpMgr] 从 window.$$user 同步获取 token (cached):${window.$$user.token}`);
        return window.$$user.token;
      }

      // 2️⃣ 缓存中没有，调用 getTokenAndKey 获取（兜底方案）
      logger("[httpMgr] No cached token, calling getTokenAndKey...");
      const userInfo = await getTokenAndKey({ needLogin: false });
      if (userInfo && userInfo.token) {
        logger(`[httpMgr] 从 getTokenAndKey 获取 token:${userInfo.token}`);
        return userInfo.token;
      }

      logger("[httpMgr] 没有 token，用户未登录");
      return null;
    } catch (error) {
      logger(`[httpMgr] 获取 token 失败:${error}`);
      return null;
    }
  }

  /**
   * ⭐ 处理客户端/小程序环境的重新登录
   * 完全参考 axios.js 的处理流程（第 98-104 行）
   */
  private async _handleClientRelogin(): Promise<void> {
    try {
      logger("[httpMgr] 开始客户端重新登录...");

      // 客户端环境：先调用 invalidToken，再调用 getTokenAndKey
      if (env.isEasyBikeApp) {
        await Native.invalidToken();
        // 让客户端处理重新登录
        Native.getTokenAndKey({ needLogin: true });
        logger("[httpMgr] 客户端调用 invalidToken 和 getTokenAndKey 触发重新登录");
      }

      // 支付宝小程序：直接调用 getTokenAndKey
      if (env.isAliMP || env.isAliMPMonitor) {
        Native.getTokenAndKey({ needLogin: false });
        logger("[httpMgr] 支付宝小程序调用 getTokenAndKey 触发重新登录");
      }

      // 强制刷新 token（从全局对象 window.$$user 获取）
      const userInfo = await getTokenAndKey({ force: true, needLogin: false });
      if (!userInfo || !userInfo.token) {
        throw new Error("重新登录失败：未获取到 token");
      }

      logger("[httpMgr] 重新登录成功");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[httpMgr] 重新登录失败:", error);
      throw error;
    }
  }

  /**
   * ⭐ 重新发起当前请求
   * Token 刷新成功后，使用新 token 重试请求
   */
  private async _retryCurrentRequest(): Promise<any> {
    // 防止无限递归重试
    if (this._curData._retryCount >= 1) {
      throw new NetError(
        "重试次数过多，请重新登录",
        NetErrorType.serverErr,
      );
    }

    this._curData._retryCount += 1;

    logger("[httpMgr] 重新发起请求 with new token:", {
      action: this._curData.action,
      retryCount: this._curData._retryCount,
    });

    // 重新获取 token
    try {
      const userInfo = await getTokenAndKey({ needLogin: false });
      if (userInfo && userInfo.token) {
        this._curData.requestParams.token = userInfo.token;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[httpMgr] 重新获取 token 失败:", error);
    }

    // 重新发起请求
    const responseData = await this._handleRequest();
    return this._handleResponse(responseData);
  }

  static hooks: {
    before: { (...args: any[]): void }[]; // 调用http之前的钩子
    after: { (...args: any[]): void }[]; // 调用http请求回来之后的钩子
  } = {
    before: [],
    after: [],
  };

  static mixin({
    before,
    after,
  }: {
    before?: { (...args: any[]): void };
    after?: { (...args: any[]): void };
  }) {
    if (before && typeof before === "function") {
      const beforeArr = HttpMgr.hooks.before;
      if (
        !beforeArr.some(
          (cellFn: { (...args: any[]): void }) => cellFn === before,
        )
      ) {
        beforeArr.push(before);
      }
    }

    if (after && typeof after === "function") {
      const afterArr = HttpMgr.hooks.after;
      if (
        !afterArr.some((cellFn: { (...args: any[]): void }) => cellFn === after)
      ) {
        afterArr.push(after);
      }
    }
  }
}

/** http接口管理器 */
export const httpMgr = new HttpMgr();
